/**
 * Prepare Chart Context Payload
 *
 * Combines multiple chart entries into a single payload for the Advisor API.
 *
 * NOTE: We do NOT minify Highcharts options before sending to DynamoDB.
 * The full config is stored as-is because:
 * - The agent accesses chart data via tools (Option 2), not via augmented message
 * - Full configs enable chart patching/re-rendering via JSON Patch
 * - DynamoDB can handle 400KB items (typical charts are 2-50KB)
 * - The minifier.ts is preserved for potential future use (e.g., size validation)
 */

import { CHART_CONTEXT_LIMITS } from './constants';
import { getJsonByteSize } from './minifier';
import type { ChartEntry, ChartContextPayload, ChartLegendMetadata } from './types';

export interface PreparePayloadOptions {
  /** Primary period label (e.g., "Jan 4, 2026 - Jan 31, 2026") */
  primaryPeriod?: string;
  /** Comparison period label */
  comparisonPeriod?: string;
  /** Selected filters */
  selectedFilters?: Record<string, string>;
}

export interface PreparePayloadResult {
  payload: ChartContextPayload;
  totalSizeBytes: number;
  wasLimited: boolean;
}

/**
 * Prepare a chart context payload from one or more Highcharts options.
 *
 * @param charts - Array of { chartId, options } to include
 * @param options - Additional context options
 * @returns Prepared payload with size info
 */
export function prepareChartContextPayload(
  charts: Array<{ chartId: string; options: Record<string, unknown>; legendMetadata?: ChartLegendMetadata }>,
  options: PreparePayloadOptions = {}
): PreparePayloadResult {
  // Limit number of charts
  const limitedCharts = charts.slice(0, CHART_CONTEXT_LIMITS.MAX_CHARTS_PER_CONTEXT);
  const wasLimited = charts.length > CHART_CONTEXT_LIMITS.MAX_CHARTS_PER_CONTEXT;

  // Pass through chart options as-is (no minification)
  // Full Highcharts configs are stored in DynamoDB for agent tool access and JSON Patch
  const chartEntries: ChartEntry[] = limitedCharts.map(({ chartId, options: chartOptions, legendMetadata }) => {
    const sizeBytes = getJsonByteSize(chartOptions);
    return {
      chartId,
      options: chartOptions,
      ...(legendMetadata ? { legendMetadata } : {}),
      _meta: {
        originalSizeBytes: sizeBytes,
        minifiedSizeBytes: sizeBytes,
        dataPointsRemoved: 0,
        wasTruncated: false,
      },
    };
  });

  // Build payload
  const payload: ChartContextPayload = {
    charts: chartEntries,
  };

  // Add filter context if provided
  if (options.primaryPeriod || options.comparisonPeriod || options.selectedFilters) {
    payload.filterContext = {};
    if (options.primaryPeriod) payload.filterContext.primaryPeriod = options.primaryPeriod;
    if (options.comparisonPeriod) payload.filterContext.comparisonPeriod = options.comparisonPeriod;
    if (options.selectedFilters) payload.filterContext.selectedFilters = options.selectedFilters;
  }

  const totalSizeBytes = getJsonByteSize(payload);

  return {
    payload,
    totalSizeBytes,
    wasLimited,
  };
}

/**
 * Validate that a payload is within size limits.
 *
 * @param payload - The payload to validate
 * @returns Validation result with details
 */
export function validatePayloadSize(payload: ChartContextPayload): {
  isValid: boolean;
  totalBytes: number;
  perChartBytes: number[];
  issues: string[];
} {
  const totalBytes = getJsonByteSize(payload);
  const perChartBytes = payload.charts.map((chart) => getJsonByteSize(chart));
  const issues: string[] = [];

  if (totalBytes > CHART_CONTEXT_LIMITS.MAX_TOTAL_BYTES) {
    issues.push(
      `Total size ${totalBytes} bytes exceeds limit of ${CHART_CONTEXT_LIMITS.MAX_TOTAL_BYTES} bytes`
    );
  }

  perChartBytes.forEach((bytes, idx) => {
    if (bytes > CHART_CONTEXT_LIMITS.MAX_PER_CHART_BYTES) {
      issues.push(
        `Chart ${idx} (${payload.charts[idx].chartId}) size ${bytes} bytes exceeds limit of ${CHART_CONTEXT_LIMITS.MAX_PER_CHART_BYTES} bytes`
      );
    }
  });

  return {
    isValid: issues.length === 0,
    totalBytes,
    perChartBytes,
    issues,
  };
}
